import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Breadcrumbs, Button, Card, CardBody, CardHeader, Spinner, Typography } from "@material-tailwind/react";
import { ArrowLeftIcon, Building2Icon, ExternalLinkIcon, HomeIcon, MapIcon, PhoneIcon } from "lucide-react";
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';
import { useGetBranchById } from "../../custom-hooks/use-branches";
import moment from "moment";
import { GetBuildings } from "../../services/backend";
import { URLS } from "../../router-x/routes";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_API_KEY || 'pk.eyJ1IjoiZmFycnVraHNoYWhpZCIsImEiOiJjbGhobnEwNWQwYWJuM2duems5cTdmZ2F5In0.VqxGVO_hETuJnKw4-ej3xA';

const BranchDetailPage = () => {
    const { branchId } = useParams();
    const navigate = useNavigate();
    const mapRef = useRef(null);
    const mapContainerRef = useRef(null);
    // const [marker, setMarker] = useState(null); !TODO: Use this to add map functionality for markers
    const [buildings, setBuildings] = useState([]);
    const [loadingBuildings, setLoadingBuildings] = useState(false);

    // Fetch branch details using custom hook
    const {
        data: branchData,
        isLoading,
        error,
    } = useGetBranchById(branchId);

    const branch = branchData?.data?.branch;

    // Initialize map when component mounts and branch data is available
    useEffect(() => {
        const fetchBuildings = async () => {
            try {
                setLoadingBuildings(true);
                const buildings = await GetBuildings(branchId);
                setBuildings(buildings.data.buildings);
            } catch (error) {
                console.error("Error fetching buildings:", error);
            } finally {
                setLoadingBuildings(false);
            }
        }
        if (!branch || !mapContainerRef.current) return;

        fetchBuildings(); // to fetch all the buildings associated with this branch
        // Create map instance
        const map = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: 'mapbox://styles/mapbox/light-v11',
            center: [branch.location?.lng || -74.5, branch.location?.lat || 40],
            zoom: 14,
        });

        mapRef.current = map;

        // Add marker if coordinates exist
        if (branch.location?.lng && branch.location?.lat) {
            new mapboxgl.Marker()
                .setLngLat([branch.location.lng, branch.location.lat])
                .setPopup(new mapboxgl.Popup().setHTML(
                    `<h3>${branch.name}</h3><p>${branch.address}</p>`
                ))
                .addTo(map);

            // setMarker(newMarker);
        }



        return () => {
            // Clean up map when component unmounts
            map.remove();
        };
    }, [branch, branchId]);

    // If there's an error, display error message
    if (error) {
        return (
            <div className="m-5 p-5 border rounded-3xl">
                <div className="flex items-center mb-4">
                    <Button
                        variant="text"
                        className="flex items-center gap-2"
                        onClick={() => navigate(URLS.ADMIN.BRANCH)}
                    >
                        <ArrowLeftIcon size={16} />
                        Back to Branches
                    </Button>
                </div>
                <div className="p-10 text-center">
                    <Typography variant="h5" color="red">
                        Error loading branch details
                    </Typography>
                    <Typography className="mt-2">
                        {error.message || "An unknown error occurred"}
                    </Typography>
                </div>
            </div>
        );
    }

    return (
        <div className="m-5 p-5 border rounded-3xl">
            {/* Breadcrumbs */}
            <Breadcrumbs className="mb-4">
                <Link to="/" className="opacity-60 flex items-center gap-1">
                    <HomeIcon size={14} />
                    <span>Home</span>
                </Link>
                <Link to={URLS.ADMIN.BRANCH} className="opacity-60 flex items-center gap-1">
                    <Building2Icon size={14} />
                    <span>Branches</span>
                </Link>
                <span className="flex items-center gap-1">
                    {isLoading ? (
                        <Spinner size="sm" />
                    ) : (
                        <>
                            <Building2Icon size={14} />
                            <span>{branch?.name || 'Branch Details'}</span>
                        </>
                    )}
                </span>
            </Breadcrumbs>

            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <Spinner />
                </div>
            ) : (
                <div className="mt-4">
                    <div className="flex justify-between items-center">
                        <Typography variant="h3" className="font-thin">
                            {branch?.name}
                        </Typography>
                        <Button
                            variant="outlined"
                            className="flex items-center gap-2 rounded-full"
                            onClick={() => navigate(URLS.ADMIN.BRANCH)}
                        >
                            <ArrowLeftIcon size={16} />
                            Back to Branches
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-8">
                        {/* Branch Information Card */}
                        <Card className="col-span-1 shadow-none border">
                            <CardHeader className="p-4 shadow-none border">
                                <Typography variant="h5" className="mb-0">
                                    Branch Information
                                </Typography>
                            </CardHeader>
                            <CardBody>
                                <div className="space-y-4">
                                    <div>
                                        <Typography variant="h6" className="mb-2">Contact Information</Typography>
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2">
                                                <PhoneIcon size={16} className="text-blue-600" />
                                                <Typography>{branch?.phone || 'N/A'}</Typography>
                                            </div>
                                            <div className="flex items-start gap-2">
                                                <Typography variant="small" className="text-blue-600 mt-1">
                                                    📧
                                                </Typography>
                                                <Typography>{branch?.email || 'N/A'}</Typography>
                                            </div>
                                            {branch?.website && (
                                                <div className="flex items-start gap-2">
                                                    <Typography variant="small" className="text-blue-600 mt-1">
                                                        🌐
                                                    </Typography>
                                                    <a
                                                        href={branch.website?.startsWith('http') ? branch.website : `https://${branch.website}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-600 hover:underline"
                                                    >
                                                        {branch.website}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <Typography variant="h6" className="mb-2">Address</Typography>
                                        <div className="space-y-2">
                                            <div className="flex items-start gap-2">
                                                <MapIcon size={16} className="text-blue-600 mt-1" />
                                                <div>
                                                    <Typography>
                                                        {branch?.address || 'N/A'}
                                                    </Typography>
                                                    <Typography>
                                                        {[branch?.city, branch?.state, branch?.postalCode].filter(Boolean).join(', ')}
                                                    </Typography>
                                                    <Typography className="capitalize">
                                                        {branch?.country || 'N/A'}
                                                    </Typography>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <Typography variant="h6" className="mb-2">Statistics</Typography>
                                        <div className="space-y-2">
                                            <div className="flex justify-between">
                                                <Typography>Building Count:</Typography>
                                                <Typography className="font-medium">
                                                    {branch?.buildingCount || '0'}
                                                </Typography>
                                            </div>
                                            <div className="flex justify-between">
                                                <Typography>Created:</Typography>
                                                <Typography className="font-medium">
                                                    {branch?.createdDate ? moment(branch.createdDate).format('lll') : 'N/A'}
                                                </Typography>
                                            </div>
                                            <div className="flex justify-between">
                                                <Typography>Last Updated:</Typography>
                                                <Typography className="font-medium">
                                                    {branch?.lastUpdated ? moment(branch.lastUpdated).format('lll') : 'N/A'}
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>

                                    {branch?.location?.googleMapsLink && (
                                        <div className="pt-2">
                                            <a
                                                href={branch.location.googleMapsLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline flex items-center gap-2"
                                            >
                                                <MapIcon size={16} />
                                                View on Google Maps
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </CardBody>
                        </Card>
                        {
                            branch.location.lat && branch.location.lng ? (
                                <Card className="col-span-1 lg:col-span-2 shadow-none">
                                    {/* <CardHeader className="p-4 shadow-none border">
                                <Typography variant="h5" className="mb-0">
                                    Location
                                </Typography>
                            </CardHeader> */}
                                    <CardBody className="p-0">
                                        <div
                                            style={{ height: '480px' }}
                                            ref={mapContainerRef}
                                            className="map-container w-full rounded-xl"
                                        />
                                    </CardBody>
                                </Card>
                            ) :
                                <Card className="col-span-1 lg:col-span-2 shadow-none border">
                                    <CardBody className="p-0">
                                        <div className="flex items-center justify-center h-48">
                                            <Typography variant="h6" className="text-gray-500 font-thin">
                                                Location not available
                                            </Typography>
                                        </div>
                                    </CardBody>
                                </Card>
                        }
                        {/* Map Card */}

                    </div>

                    {/* Buildings Section */}
                    <Card className="mt-8 shadow-none border">
                        <CardHeader className="p-4 shadow-none border">
                            <Typography variant="h5" className="mb-0">
                                Buildings
                            </Typography>
                        </CardHeader>
                        <CardBody>
                            {branch?.buildingCount && parseInt(branch.buildingCount) > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {/* This would normally be populated with actual building data */
                                        loadingBuildings ? (
                                            <Spinner />
                                        ) : (
                                            buildings.map((building) => (
                                                <Card key={building._id} className="shadow-none border">
                                                    <CardBody>
                                                        <div className="flex justify-between items-center">
                                                            <div className="flex items-center gap-2">
                                                                <Building2Icon size={16} />
                                                                <Typography variant="h6">{building.name}</Typography>
                                                                <Typography variant="small">({building.buildingCode})</Typography>
                                                            </div>
                                                            <Link to={`${URLS.ADMIN.BUILDING}/${building._id}`}>
                                                                <Button variant="outlined" className="rounded-full p-2">
                                                                    <ExternalLinkIcon size={16} />
                                                                </Button>
                                                            </Link>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            )))
                                    }
                                </div>
                            ) : (
                                <div className="text-center py-6">
                                    <Typography>No buildings have been added to this branch yet.</Typography>
                                    <Button className="mt-4 rounded-full">
                                        Add Building
                                    </Button>
                                </div>
                            )}
                        </CardBody>
                    </Card>

                    <Card className="mt-8 shadow-none border">
                        <CardBody>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Typography variant="h6" className="mb-2">Created At</Typography>
                                    <Typography variant="small" className="mt-2 text-gray-600">
                                        {branch?.createdDate ? moment(branch.createdDate).format('lll') : 'N/A'} By {branch?.createdBy?.name}
                                    </Typography>
                                </div>
                                <div>
                                    <Typography variant="h6" className="mb-2">Last Updated</Typography>
                                    <Typography variant="small" className="mt-2 text-gray-600">
                                        {branch?.lastUpdated ? moment(branch.lastUpdated).format('lll') : 'N/A'} By {branch?.updatedBy?.name}
                                    </Typography>
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Branch Metrics Card */}
                    {/* <Card className="mt-8 shadow-none border">
                        <CardHeader className="p-4 shadow-none border">
                            <Typography variant="h5" className="mb-0">
                                Branch Metrics
                            </Typography>
                        </CardHeader>
                        <CardBody>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <Card className="shadow-sm">
                                    <CardBody className="text-center">
                                        <Typography variant="h6" className="mb-2">Total Capacity</Typography>
                                        <Typography variant="h4" color="blue">0</Typography>
                                        <Typography variant="small" className="mt-2 text-gray-600">
                                            Total seats across all buildings
                                        </Typography>
                                    </CardBody>
                                </Card>

                                <Card className="shadow-sm">
                                    <CardBody className="text-center">
                                        <Typography variant="h6" className="mb-2">Occupancy Rate</Typography>
                                        <Typography variant="h4" color="green">0%</Typography>
                                        <Typography variant="small" className="mt-2 text-gray-600">
                                            Current branch utilization
                                        </Typography>
                                    </CardBody>
                                </Card>

                                <Card className="shadow-sm">
                                    <CardBody className="text-center">
                                        <Typography variant="h6" className="mb-2">Revenue</Typography>
                                        <Typography variant="h4" color="purple">PKR 0</Typography>
                                        <Typography variant="small" className="mt-2 text-gray-600">
                                            Monthly revenue generated
                                        </Typography>
                                    </CardBody>
                                </Card>
                            </div>
                        </CardBody>
                    </Card> */}
                </div>
            )}
        </div>
    );
};

export default BranchDetailPage;